import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { FileText, Film, ImageIcon, Paperclip, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BugReportFileUploaderProps {
  files: File[]
  onChange: (files: File[]) => void
  maxFiles?: number
  disabled?: boolean
}

function fileIcon(type: string) {
  if (type.startsWith('video/')) return Film
  if (type.startsWith('image/')) return ImageIcon
  return FileText
}

export function BugReportFileUploader({
  files,
  onChange,
  maxFiles = 3,
  disabled,
}: BugReportFileUploaderProps) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      const remaining = maxFiles - files.length
      const next = accepted.slice(0, remaining)
      if (next.length > 0) onChange([...files, ...next])
    },
    [files, maxFiles, onChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || files.length >= maxFiles,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'],
      'video/*': ['.mp4', '.webm', '.mov'],
    },
    maxFiles: maxFiles - files.length,
  })

  const remove = (index: number) => {
    onChange(files.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3">
      <div
        {...getRootProps()}
        className={cn(
          'cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors',
          isDragActive
            ? 'border-gold-400/50 bg-gold-500/5'
            : 'border-luxury-600 hover:border-gold-400/35 hover:bg-luxury-800/40',
          (disabled || files.length >= maxFiles) && 'pointer-events-none opacity-50'
        )}
      >
        <input {...getInputProps()} />
        <Paperclip className="mx-auto mb-2 h-7 w-7 text-gold-400/70" />
        <p className="text-sm text-zinc-300">
          {isDragActive ? 'Drop files here' : 'Attach image or video (optional)'}
        </p>
        <p className="mt-1 text-xs text-zinc-500">
          Images or video · up to {maxFiles} files · 10 MB each
        </p>
      </div>

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file, i) => {
            const Icon = fileIcon(file.type)
            return (
              <li
                key={`${file.name}-${i}`}
                className="flex items-center gap-3 rounded-lg border border-luxury-700 bg-luxury-850/50 px-3 py-2"
              >
                <Icon className="h-4 w-4 shrink-0 text-gold-400" />
                <div className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm text-zinc-200">{file.name}</p>
                  <p className="text-[10px] text-zinc-500">
                    {(file.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="rounded p-1 text-zinc-500 hover:bg-luxury-700 hover:text-red-400"
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
